import moment from 'moment';
import { LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION } from '../../../../labTestsConstants';
import { Data, LabVitalType, TableDataType } from '../../../../store/types/LabTests';
import { LabVitalEntity } from '../../../../store/types/prescription';

interface AllKeysType {
    [key: string]: boolean;
}
export const getAllLabTestsVitalKeys = (data: Data[]): AllKeysType => {
    const allKeys: { [key: string]: boolean } = {};

    data.forEach((labTest) => {
        labTest.labVitals.forEach((labVital) => {
            allKeys[labVital.id] = true;
        });
    });

    return allKeys;
};

export interface AllKeysNameType {
    id: string;
    name: string;
    unit_dislay_name: string;
}

export const getAllLabTestsVitalKeysAndName = (data: Data[]): AllKeysNameType[] => {
    const allKeys: AllKeysNameType[] = [];

    data.forEach((labTest) => {
        labTest.labVitals.forEach((labVital) => {
            allKeys.push({
                id: labVital?.id,
                name: labVital?.name,
                unit_dislay_name: labVital?.unit_dislay_name || '',
            });
        });
    });

    return allKeys;
};

export const getAllLabVitalKeysNotPresentInLabTest = (
    allKeys: AllKeysType,
    labVitals?: LabVitalEntity[],
) => {
    const extraVitals: LabVitalEntity[] = [];

    labVitals?.forEach((labVital) => {
        if (!allKeys[labVital.id]) {
            extraVitals.push(labVital);
        }
    });

    return extraVitals;
};

export const buildDateWiseLabVitalArray = (data?: LabVitalEntity[]) => {
    interface VitalObject {
        data: LabVitalEntity[];
        count: number;
    }

    const map: {
        [key: string]: { [key: string]: VitalObject | number };
    } = {};

    data?.forEach(function (obj) {
        const key = moment(obj.date)?.format('YYYY-MM-DD');

        if (!map[key]) {
            map[key] = { maxCount: 0 };
        }

        if (map[key][obj.id]) {
            (map[key][obj.id] as VitalObject).data.push(obj);

            (map[key][obj.id] as VitalObject).count = (map[key][obj.id] as VitalObject).count + 1;
        } else {
            map[key][obj.id] = { data: [obj], count: 1 };
        }

        map[key]['maxCount'] = Math.max(
            (map[key][obj.id] as VitalObject).count,
            map[key]['maxCount'] as number,
        );
    });

    const dateArray: { date: string; data: { [key: string]: LabVitalEntity } }[] = [];

    Object.keys(map).forEach((date) => {
        const maxCount = map[date]?.maxCount;
        delete map[date]?.maxCount;
        // @ts-expect-error ts version upgrade
        for (let i = 0; i < maxCount; i++) {
            const obj: { date: string; data: { [key: string]: LabVitalEntity } } = {
                date: date,
                data: {},
            };

            Object.keys(map[date]).forEach((eleKey) => {
                if ((map[date][eleKey] as VitalObject).data[i]) {
                    obj.data[(map[date][eleKey] as VitalObject).data[i].id] = (map[date][
                        eleKey
                    ] as VitalObject).data[i];
                }
            });

            dateArray.push(obj);
        }
    });
    return dateArray;
};

export const tranformData = (
    d: (Data | TableDataType)[],
    data: Data[],
    addNewColumn?: boolean,
    addNColumns: number = 1,
) => {
    if (addNewColumn) {
        return (d as TableDataType[]).map((d, i: number) => ({
            ...d,
            labVitals: d.labVitals.map((vital, j: number) => [...vital, data[i].labVitals[j]]),
        }));
    }

    return (d as Data[]).map((d) => ({
        ...d,
        labVitals: d.labVitals.map((vital) => [
            ...(Array.from({ length: addNColumns }, () => vital) || []),
        ]),
    }));
};

export const updateElementAndGetTableObj = (
    tableData: TableDataType[],
    labVitalObj: Partial<LabVitalType>,
    i: number,
) => {
    return tableData.map((labTest) => {
        return {
            ...labTest,
            labVitals: labTest.labVitals.map((vital) => {
                if (vital[0].id === labVitalObj.id) {
                    return vital.map((v, l: number) => {
                        if (l === i) {
                            return {
                                ...v,
                                ...labVitalObj,
                            };
                        }
                        return v;
                    });
                }
                return vital;
            }),
        };
    }) as TableDataType[];
};

export const calculateTotalFilledElementsInOneLabTests = (
    tableData: TableDataType[],
    dateColumns: (string | undefined)[],
    id?: string,
) => {
    const labVitals = tableData?.find((data) => data.id === id)?.labVitals;

    if (!labVitals) {
        return 0;
    }

    let count = 0;

    for (let i = 0; i < dateColumns.length; i += 1) {
        for (let j = 0; j < labVitals.length; j += 1) {
            if (dateColumns[i]) {
                if (labVitals?.[j]?.[i]?.value) {
                    count += 1;
                }
            }
        }
    }

    return count;
};

export const filterAllFilledTableData = (
    dateColumns: (string | undefined)[],
    tableData: TableDataType[],
) => {
    const data: LabVitalType[] = [];

    for (let i = 0; i < dateColumns.length; i += 1) {
        const uniquiIdSet = new Set();
        for (let j = 0; j < tableData.length; j += 1) {
            for (let k = 0; k < tableData[j].labVitals.length; k += 1) {
                if (dateColumns[i] && !uniquiIdSet.has(tableData[j]?.labVitals?.[k]?.[i]?.id)) {
                    if (tableData[j]?.labVitals?.[k]?.[i]?.isComingFromPP) {
                        const d = { ...tableData[j]?.labVitals?.[k]?.[i] };
                        delete d.isComingFromPP;

                        data.push({
                            ...d,
                            date: moment(dateColumns[i]).toISOString(),
                        });
                    } else if (tableData[j]?.labVitals?.[k]?.[i]?.value) {
                        data.push({
                            ...tableData[j].labVitals[k][i],
                            date: moment(dateColumns[i]).toISOString(),
                        });
                    }
                }
                uniquiIdSet.add(tableData[j]?.labVitals?.[k]?.[i]?.id);
            }
        }
    }

    return data;
};

export const calculateOutOfRange = (
    dateColumns: (string | undefined)[],
    tableData: TableDataType[],
) => {
    let count = 0;

    for (let i = 0; i < dateColumns.length; i += 1) {
        const uniquiIdSet = new Set();
        for (let j = 0; j < tableData.length; j += 1) {
            for (let k = 0; k < tableData[j].labVitals.length; k += 1) {
                if (dateColumns[i] && !uniquiIdSet.has(tableData[j]?.labVitals?.[k]?.[i]?.id)) {
                    const isHighLowAbnormalInterpretation =
                        LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION[
                            tableData?.[j]?.labVitals?.[k]?.[i]?.interpretation
                                ?.id as keyof typeof LAB_VITAL_LOW_HIGH_ABNORMAL_INTERPRETATION
                        ];

                    if (
                        tableData?.[j]?.labVitals?.[k]?.[i]?.value &&
                        isHighLowAbnormalInterpretation
                    ) {
                        count += 1;
                    }
                }
                uniquiIdSet.add(tableData[j]?.labVitals?.[k]?.[i]?.id);
            }
        }
    }

    return count;
};

export const calculateTotalFilledElements = (
    dateColumns: (string | undefined)[],
    tableData: TableDataType[],
) => {
    let count = 0;

    for (let i = 0; i < dateColumns.length; i += 1) {
        const uniquiIdSet = new Set();
        for (let j = 0; j < tableData.length; j += 1) {
            for (let k = 0; k < tableData[j].labVitals.length; k += 1) {
                if (dateColumns[i] && !uniquiIdSet.has(tableData[j]?.labVitals?.[k]?.[i]?.id)) {
                    if (tableData?.[j]?.labVitals?.[k]?.[i]?.value) {
                        count += 1;
                    }
                }
                uniquiIdSet.add(tableData[j]?.labVitals?.[k]?.[i]?.id);
            }
        }
    }

    return count;
};

export const filterTableDataBasedOnFuseSearchSet = (
    searchResult: Set<string> | undefined,
    tableData: TableDataType[],
) => {
    return searchResult
        ? (tableData
              .map((item) => {
                  const filteredChildren = item.labVitals.filter((vital) => {
                      return searchResult.has(vital[0].id);
                  });

                  if (!filteredChildren?.length) {
                      return;
                  }

                  return {
                      ...item,
                      labVitals: filteredChildren,
                  };
              })
              .filter(Boolean) as TableDataType[])
        : tableData;
};

export const transformAccordingToTableDataSource = (
    filteredData: TableDataType[],
    dateColumns: (string | undefined)[],
) => {
    return filteredData.map((labTest, i: number) => ({
        key: i,
        ...labTest,
        type: 'parent-table',
        children: labTest.labVitals.map((vital) => ({
            ...vital,
            id: labTest.id,
            disabledColumn: dateColumns,
        })),
    })) as TableDataType[];
};
